import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

const publicLinks = [
  { to: "/lawyers", label: "Find Lawyers" },
  { to: "/#how-it-works", label: "How it Works" },
  { to: "/#pricing", label: "Pricing" },
  { to: "/chat", label: "Get Free Guidance" },
];

export const Header = () => {
  const { user, login, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const loc = useLocation();

  return (
    <header
      data-testid="site-header"
      className="sticky top-0 z-50 bg-white/90 backdrop-blur-xl border-b border-slate-200"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
          <img
            src="/advoset-logo.png"
            alt="Advoset"
            className="h-10 w-10 object-contain"
            loading="eager"
          />
          <div className="flex flex-col leading-none">
            <span className="font-extrabold text-lg text-[#0A2540] tracking-tight" style={{ fontFamily: "Poppins" }}>Advoset</span>
            <span className="text-[10px] font-semibold tracking-[0.15em] text-[#D4AF37] uppercase">Legal Help, Simplified</span>
          </div>
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          {publicLinks.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={`text-sm font-semibold transition-colors ${
                loc.pathname.startsWith(l.to) ? "text-[#0A2540]" : "text-slate-700 hover:text-[#0A2540]"
              }`}
            >
              {l.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="user-menu-trigger" className="hidden md:flex items-center gap-2 rounded-full border border-slate-200 px-2 py-1 hover:bg-slate-50">
                  {user.picture ? (
                    <img src={user.picture} alt="" className="h-7 w-7 rounded-full" />
                  ) : (
                    <div className="h-7 w-7 rounded-full bg-[#0A2540] text-[#D4AF37] text-xs grid place-items-center font-bold">
                      {user.name?.[0]?.toUpperCase() || "U"}
                    </div>
                  )}
                  <span className="text-sm font-semibold text-slate-800 pr-1 max-w-[120px] truncate">{user.name}</span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuItem asChild data-testid="menu-dashboard">
                  <Link to={user.role === "lawyer" ? "/lawyer-dashboard" : "/dashboard"}>
                    {user.role === "lawyer" ? "Lawyer Dashboard" : "My Dashboard"}
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild data-testid="menu-profile">
                  <Link to="/dashboard?tab=profile">My Profile</Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild data-testid="menu-chats">
                  <Link to="/chat">AI Chats</Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild data-testid="menu-consults">
                  <Link to="/dashboard?tab=consults">My Consultations</Link>
                </DropdownMenuItem>
                {user.role === "admin" && (
                  <DropdownMenuItem asChild data-testid="menu-admin">
                    <Link to="/admin">Admin Panel</Link>
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={logout} data-testid="menu-logout" className="text-red-600">
                  Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Button
              onClick={login}
              data-testid="header-login-btn"
              className="hidden md:inline-flex rounded-full adv-btn-primary font-semibold px-5"
            >
              Sign in / Sign up
            </Button>
          )}

          <Link to="/lawyers" className="hidden sm:inline-flex">
            <Button data-testid="header-consult-cta" className="rounded-full adv-btn-gold font-bold px-5">
              Consult Now
            </Button>
          </Link>

          <button
            className="md:hidden p-2 rounded-md hover:bg-slate-100"
            onClick={() => setOpen((x) => !x)}
            data-testid="mobile-menu-toggle"
            aria-label="Menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="md:hidden border-t border-slate-200 bg-white">
          <div className="px-4 py-3 flex flex-col gap-1">
            {publicLinks.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className="px-3 py-2 rounded-lg text-sm font-semibold text-slate-700 hover:bg-slate-50"
              >
                {l.label}
              </Link>
            ))}
            {user ? (
              <>
                <Link to={user.role === "lawyer" ? "/lawyer-dashboard" : "/dashboard"} onClick={() => setOpen(false)} className="px-3 py-2 rounded-lg text-sm font-semibold text-slate-700 hover:bg-slate-50" data-testid="mobile-nav-dashboard">
                  Dashboard
                </Link>
                {user.role === "admin" && (
                  <Link to="/admin" onClick={() => setOpen(false)} className="px-3 py-2 rounded-lg text-sm font-semibold text-slate-700 hover:bg-slate-50" data-testid="mobile-nav-admin">
                    Admin Panel
                  </Link>
                )}
                <button onClick={logout} className="px-3 py-2 text-left rounded-lg text-sm font-semibold text-red-600 hover:bg-red-50" data-testid="mobile-logout">
                  Log out
                </button>
              </>
            ) : (
              <Button onClick={login} className="mt-2 rounded-full adv-btn-primary" data-testid="mobile-login-btn">
                Sign in / Sign up
              </Button>
            )}
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
